import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, TOKEN_KEY, formatErr } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadBilling = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/status");
      setBilling(data);
    } catch {
      setBilling(null);
    }
  }, []);

  const fetchMe = useCallback(async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setCompany(data.company);
      await loadBilling();
    } catch {
      localStorage.removeItem(TOKEN_KEY);
    } finally {
      setLoading(false);
    }
  }, [loadBilling]);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      setCompany(data.company);
      await loadBilling();
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatErr(e.response?.data?.detail) || e.message };
    }
  };

  const signup = async (payload) => {
    try {
      const { data } = await api.post("/auth/signup", payload);
      localStorage.setItem(TOKEN_KEY, data.token);
      setUser(data.user);
      setCompany(data.company);
      await loadBilling();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatErr(e.response?.data?.detail) || e.message };
    }
  };

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setUser(null); setCompany(null); setBilling(null);
  };

  return (
    <AuthContext.Provider value={{ user, company, billing, loading, login, signup, logout, refreshBilling: loadBilling }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
